import { useContext, useEffect, useMemo, useState } from "react";
import Drawer from "@mui/material/Drawer";
import { Box, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "../../content/pages/homepage.module.css";
import { isIOS, isAndroid } from "src/utils/platformUtils";
import { WidgetContext } from "src/contexts/WidgetContext";
import { UserRequestHistoryItem } from "src/api/fetchUserRequestHistory";
import {
  filterHistoryItemsByKey,
  getHistoryEndpointLabel,
  getHistoryMenuOptions,
  HistoryEndpointKey,
} from "src/utils/userRequestHistoryUi";
import { HistoryMenuMain } from "./HistoryMenuMain";
import HistoryEndpointDetail from "./HistoryEndpointDetail";

interface HistoryDrawerProps {
  open: boolean;
  onClose: () => void;
  items: UserRequestHistoryItem[];
}

/**
 * History drawer — same structure as HelpMenu (bottom drawer + menu → detail),
 * inverted colors (black bg / white text). Menu options are driven by history items.
 */
const HistoryDrawer = ({ open, onClose, items }: HistoryDrawerProps) => {
  const { aiSettings } = useContext(WidgetContext);
  const [activeView, setActiveView] = useState<"menu" | HistoryEndpointKey>("menu");

  const menuOptions = useMemo(() => getHistoryMenuOptions(items), [items]);

  // Always land on menu when opening (fresh fetch or reopen)
  useEffect(() => {
    if (open) setActiveView("menu");
  }, [open]);

  const handleClose = () => {
    setActiveView("menu");
    onClose();
  };

  const handleBack = () => {
    setActiveView("menu");
  };

  const getTitle = (): string => {
    if (activeView === "menu") return "History";
    return getHistoryEndpointLabel(activeView);
  };

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={handleClose}
      hideBackdrop={true}
      sx={{
        pointerEvents: "none",
      }}
      ModalProps={{
        keepMounted: true,
        sx: {
          pointerEvents: "auto",
          top: isIOS || isAndroid ? "calc(env(safe-area-inset-top) + 56px)" : "56px",
          zIndex: 1500,
        },
      }}
      PaperProps={{
        sx: {
          pointerEvents: "auto",
          width: {
            xs: "100%",
            sm: "720px",
            md: "720px",
            lg: "720px",
          },
          margin: {
            xs: "0",
            sm: "0 auto",
            md: "0 auto",
            lg: "0 auto",
          },
          height: isIOS || isAndroid
            ? (aiSettings.quan ? "calc(100svh - env(safe-area-inset-top))" : "calc(100svh - env(safe-area-inset-top) - 56px)")
            : (!aiSettings.quan ? "calc(100vh - 56px)" : "100%"),
          border: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
          borderTopLeftRadius: "10px",
          borderTopRightRadius: "10px",
          bgcolor: "#000000",
        },
      }}
    >
      <Box
        className={styles.noScrollbar}
        sx={{
          bgcolor: "#000000",
          height: "100%",
          position: "relative",
          // Allow text selection on desktop; pan-y only for touch scroll
          userSelect: "text",
          WebkitUserSelect: "text",
          touchAction: "pan-y",
          paddingBottom: isAndroid ? "max(env(safe-area-inset-bottom), 15px)" : "0px",
          pointerEvents: "auto",
        }}
      >
        {/* Header */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1000,
            backgroundColor: "#000000",
            borderBottom: `1px solid ${PureLightThemeCaaS.colors.alpha.white[30]}`,
          }}
        >
          <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center" p={1}>
            <IconButton
              aria-label="back"
              onClick={handleBack}
              sx={{
                visibility: activeView === "menu" ? "hidden" : "visible",
              }}
            >
              <ArrowBackIcon sx={{ color: "#ffffff" }} />
            </IconButton>
            <Typography variant="h4" fontSize={"16px"} color="#ffffff">
              {getTitle()}
            </Typography>
            <IconButton aria-label="close" onClick={handleClose}>
              <CloseIcon sx={{ color: "#ffffff" }} />
            </IconButton>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: "auto", minHeight: 0, WebkitOverflowScrolling: "touch" }}>
          {activeView === "menu" && (
            <HistoryMenuMain
              options={menuOptions}
              onSelectEndpoint={(key) => setActiveView(key)}
            />
          )}
          {activeView !== "menu" && (
            <HistoryEndpointDetail items={filterHistoryItemsByKey(items, activeView)} />
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default HistoryDrawer;
